/**
 * Cookie Service Utility
 *
 * Provides functions to manage cookies at the domain level,
 * enabling authentication state to be shared across all subdomains.
 *
 * Example: Setting a cookie on .tweek.io makes it accessible from
 * both app.tweek.io and john.tweek.io
 */

/**
 * Gets the root domain for cookie sharing across subdomains
 *
 * @example
 * // On john.tweek.io or app.tweek.io
 * getRootDomain() // Returns ".tweek.io"
 *
 * // On localhost
 * getRootDomain() // Returns "localhost"
 */
export const getRootDomain = (): string => {
  const hostname = window.location.hostname;

  // For localhost, return as-is (cookies work without domain attribute)
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return hostname;
  }

  // For production domains like john.tweek.io or app.tweek.io
  // Extract the root domain (e.g., .tweek.io)
  const parts = hostname.split(".");

  // If we have at least 2 parts (e.g., tweek.io), return the root domain
  if (parts.length >= 2) {
    // Return .domain.tld format for subdomain sharing
    return `.${parts.slice(-2).join(".")}`;
  }

  return hostname;
};

/**
 * Cookie options for setting cookies
 */
interface CookieOptions {
  /** Number of days until the cookie expires */
  days?: number;
  /** Path for the cookie (default: "/") */
  path?: string;
  /** Whether the cookie requires HTTPS (default: true in production) */
  secure?: boolean;
  /** SameSite attribute (default: "Lax") */
  sameSite?: "Strict" | "Lax" | "None";
}

/**
 * Sets a cookie at the domain level for cross-subdomain access
 *
 * @param name - Cookie name
 * @param value - Cookie value (will be URI encoded)
 * @param options - Cookie options
 *
 * @example
 * setCookie("token", "abc123", { days: 7 });
 * setCookie("user", JSON.stringify(userData), { days: 7 });
 */
export const setCookie = (
  name: string,
  value: string,
  options: CookieOptions = {}
): void => {
  const { days = 7, path = "/", secure, sameSite = "Lax" } = options;

  const rootDomain = getRootDomain();
  const isLocalhost =
    rootDomain === "localhost" || rootDomain === "127.0.0.1";

  // Calculate expiry date
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;

  // Build cookie string
  let cookieString = `${name}=${encodeURIComponent(value)}; ${expires}; path=${path}; SameSite=${sameSite}`;

  // Add domain attribute for production (not needed for localhost)
  if (!isLocalhost) {
    cookieString += `; domain=${rootDomain}`;
  }

  // Add secure flag (default true in production, false for localhost)
  const isSecure = secure ?? !isLocalhost;
  if (isSecure) {
    cookieString += "; Secure";
  }

  document.cookie = cookieString;
};

/**
 * Gets a cookie value by name
 *
 * @param name - Cookie name
 * @returns Cookie value (URI decoded) or null if not found
 *
 * @example
 * const token = getCookie("token");
 * const user = JSON.parse(getCookie("user") || "null");
 */
export const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(";");

  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.startsWith(nameEQ)) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }

  return null;
};

/**
 * Removes a cookie by setting it to expire immediately
 *
 * @param name - Cookie name to remove
 *
 * @example
 * removeCookie("token");
 */
export const removeCookie = (name: string): void => {
  const rootDomain = getRootDomain();
  const isLocalhost =
    rootDomain === "localhost" || rootDomain === "127.0.0.1";

  // To delete a cookie, we set it with an expired date
  let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;

  if (!isLocalhost) {
    cookieString += `; domain=${rootDomain}`;
  }

  document.cookie = cookieString;
};

// ============================================
// Auth-specific cookie helpers
// ============================================

const AUTH_TOKEN_KEY = "auth_token";
const AUTH_USER_KEY = "auth_user";
const AUTH_EMAIL_KEY = "auth_email";

/**
 * Stores authentication data in domain-level cookies
 *
 * @param data - User data object containing token, email, and other user info
 *
 * @example
 * setAuthCookies({
 *   token: "jwt_token_here",
 *   email: "user@example.com",
 *   fullName: "John Doe",
 *   // ... other user data
 * });
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const setAuthCookies = (data: Record<string, any>): void => {
  const { token, email } = data;

  // Store token separately for easy access
  setCookie(AUTH_TOKEN_KEY, token, { days: 7 });

  // Store email separately
  setCookie(AUTH_EMAIL_KEY, email, { days: 7 });

  // Store full user data as JSON
  setCookie(AUTH_USER_KEY, JSON.stringify(data), { days: 7 });
};

/**
 * Gets the authentication token from cookies
 *
 * @returns The auth token or null if not found
 */
export const getAuthToken = (): string | null => {
  return getCookie(AUTH_TOKEN_KEY);
};

/**
 * User details interface matching the expected structure from login
 */
export interface UserDetails {
  email: string;
  fullName: string;
  token: string;
  phone?: string;
  expiresIn?: string;
  country?: string;
  roles?: string;
}

/**
 * Gets the user data from cookies
 *
 * @returns Parsed user data object or null if not found
 */
export const getAuthUser = (): UserDetails | null => {
  const userStr = getCookie(AUTH_USER_KEY);
  if (!userStr) return null;

  try {
    return JSON.parse(userStr) as UserDetails;
  } catch {
    return null;
  }
};

/**
 * Gets the user email from cookies
 *
 * @returns The user email or null if not found
 */
export const getAuthEmail = (): string | null => {
  return getCookie(AUTH_EMAIL_KEY);
};

/**
 * Clears all authentication cookies
 *
 * Call this on logout to remove auth state across all subdomains
 */
export const clearAuthCookies = (): void => {
  removeCookie(AUTH_TOKEN_KEY);
  removeCookie(AUTH_USER_KEY);
  removeCookie(AUTH_EMAIL_KEY);
};

/**
 * Checks if user is authenticated based on cookie presence
 *
 * @returns true if auth token exists in cookies
 */
export const isAuthenticatedViaCookie = (): boolean => {
  return !!getAuthToken();
};
